import { Controller } from '@hotwired/stimulus'

declare const $: (selector: Element | string) => {
  modal: (action: string) => void
  on: (event: string, handler: () => void) => void
}

const STEPS = ['who', 'how', 'due'] as const
type Step = typeof STEPS[number]

const REFUSED_CLASS = 'is-refused'
const REFUSED_MS = 260
const SEARCH_DEBOUNCE_MS = 300

type Mode = 'directory' | 'oneoff'

interface Recipient {
  id: string
  type: string
  name: string
  meta: string
  organisation: string
  email: string
  mobile: string
}

export default class FormRequestWizardController extends Controller<HTMLFormElement> {
  static targets = [
    'panel', 'dot', 'dotBar', 'backButton', 'nextButton',
    'modeButton', 'directoryPane', 'oneOffPane',
    'directorySearch', 'directoryResults', 'contactInput', 'profileInput',
    'name', 'organisation', 'email', 'mobile',
    'channelInput', 'channelCard', 'channelReason',
    'duePreset', 'dueDate', 'log', 'logNote', 'remindersWrap', 'remindersNote',
  ]

  static values = {
    searchUrl: String,
    cancelLabel: String,
    backLabel: String,
    nextLabel: String,
    sendEmailLabel: String,
    sendSmsLabel: String,
    sendCopyLabel: String,
    toLabel: String,
    noEmailLabel: String,
    noMobileLabel: String,
    logNoteEmpty: String,
    logNoteSelected: String,
    directoryEmptyLabel: String,
    directoryNoMatchLabel: String,
    directorySwitchLabel: String,
    directoryNoEmailLabel: String,
    directoryNoMobileLabel: String,
  }

  declare readonly panelTargets: HTMLElement[]
  declare readonly dotTargets: HTMLElement[]
  declare readonly dotBarTargets: HTMLElement[]
  declare readonly backButtonTarget: HTMLButtonElement
  declare readonly nextButtonTarget: HTMLButtonElement
  declare readonly modeButtonTargets: HTMLButtonElement[]
  declare readonly directoryPaneTarget: HTMLElement
  declare readonly oneOffPaneTarget: HTMLElement
  declare readonly directorySearchTarget: HTMLInputElement
  declare readonly directoryResultsTarget: HTMLElement
  declare readonly contactInputTarget: HTMLInputElement
  declare readonly profileInputTarget: HTMLInputElement
  declare readonly nameTarget: HTMLInputElement
  declare readonly organisationTarget: HTMLInputElement
  declare readonly emailTarget: HTMLInputElement
  declare readonly mobileTarget: HTMLInputElement
  declare readonly channelInputTarget: HTMLInputElement
  declare readonly channelCardTargets: HTMLElement[]
  declare readonly channelReasonTargets: HTMLElement[]
  declare readonly duePresetTarget: HTMLSelectElement
  declare readonly dueDateTarget: HTMLInputElement
  declare readonly logTarget: HTMLSelectElement
  declare readonly logNoteTarget: HTMLElement
  declare readonly remindersWrapTarget: HTMLElement
  declare readonly remindersNoteTarget: HTMLElement

  declare readonly searchUrlValue: string
  declare readonly cancelLabelValue: string
  declare readonly backLabelValue: string
  declare readonly nextLabelValue: string
  declare readonly sendEmailLabelValue: string
  declare readonly sendSmsLabelValue: string
  declare readonly sendCopyLabelValue: string
  declare readonly toLabelValue: string
  declare readonly noEmailLabelValue: string
  declare readonly noMobileLabelValue: string
  declare readonly logNoteEmptyValue: string
  declare readonly logNoteSelectedValue: string
  declare readonly directoryEmptyLabelValue: string
  declare readonly directoryNoMatchLabelValue: string
  declare readonly directorySwitchLabelValue: string
  declare readonly directoryNoEmailLabelValue: string
  declare readonly directoryNoMobileLabelValue: string

  private stepIndex = 0
  private mode: Mode = 'directory'
  private pickedRecipient: Recipient | null = null
  private searchTimer: number | undefined
  private searchRequestId = 0

  connect (): void {
    const modal = this.element.closest('.modal')

    if (modal) {
      $(modal).on('show.bs.modal', () => this.reset())
    }

    this.reset()
  }

  chooseMode (event: Event): void {
    const button = event.currentTarget as HTMLElement
    const mode = (button.dataset.mode as Mode | undefined) ?? 'directory'

    this.setMode(mode)
  }

  searchDirectory (): void {
    window.clearTimeout(this.searchTimer)
    this.searchTimer = window.setTimeout(() => this.runSearch(), SEARCH_DEBOUNCE_MS)
  }

  refreshWho (): void {
    this.refreshChannels()
    this.syncNav()
  }

  chooseChannel (event: Event): void {
    this.selectChannelCard(event.currentTarget as HTMLElement)
  }

  channelKeydown (event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    this.selectChannelCard(event.currentTarget as HTMLElement)
  }

  changeDuePreset (): void {
    const preset = this.duePresetTarget.value

    if (preset === 'custom') {
      this.dueDateTarget.classList.remove('d-none')
    } else {
      this.dueDateTarget.classList.add('d-none')
      this.dueDateTarget.value = this.dateFromToday(parseInt(preset, 10))
    }

    this.syncNav()
  }

  refreshDue (): void {
    this.syncNav()
  }

  changeLog (): void {
    const selected = this.logTarget.selectedOptions[0] ?? null
    const name = selected ? selected.value : ''

    this.logNoteTarget.textContent = name
      ? this.logNoteSelectedValue.replace('%log%', selected?.textContent ?? '')
      : this.logNoteEmptyValue
  }

  next (event: Event): void {
    if (!this.stepValid(STEPS[this.stepIndex])) {
      event.preventDefault()

      return
    }

    if (this.stepIndex < STEPS.length - 1) {
      event.preventDefault()
      this.showStep(this.stepIndex + 1)
    }
  }

  back (event: Event): void {
    event.preventDefault()

    if (this.stepIndex === 0) {
      const modal = this.element.closest('.modal')

      if (modal) {
        $(modal).modal('hide')
      }

      return
    }

    this.showStep(this.stepIndex - 1)
  }

  private setMode (mode: Mode): void {
    this.mode = mode

    this.modeButtonTargets.forEach(button => {
      button.classList.toggle('active', button.dataset.mode === mode)
    })

    this.directoryPaneTarget.classList.toggle('d-none', mode !== 'directory')
    this.oneOffPaneTarget.classList.toggle('d-none', mode !== 'oneoff')

    this.syncRecipientInputs()
    this.refreshChannels()
    this.syncNav()
  }

  private syncRecipientInputs (): void {
    const picked = this.mode === 'directory' ? this.pickedRecipient : null

    this.contactInputTarget.value = picked?.type === 'contact' ? picked.id : ''
    this.profileInputTarget.value = picked?.type === 'user' ? picked.id : ''
  }

  private switchToOneOffFromSearch (): void {
    const typed = this.directorySearchTarget.value.trim()

    this.setMode('oneoff')

    if (typed !== '' && this.nameTarget.value.trim() === '') {
      this.nameTarget.value = typed
    }

    this.nameTarget.focus()
    this.refreshWho()
  }

  private runSearch (): void {
    const query = this.directorySearchTarget.value.trim()
    const requestId = ++this.searchRequestId

    fetch(`${this.searchUrlValue}?q=${encodeURIComponent(query)}`, {
      headers: { Accept: 'application/json' },
    })
      .then(response => (response.ok ? response.json() : { results: [] }))
      .then((data: { results?: Recipient[] }) => {
        if (requestId !== this.searchRequestId) {
          return
        }

        this.renderDirectoryResults(data.results ?? [], query)
      })
      .catch(() => {
        if (requestId === this.searchRequestId) {
          this.renderDirectoryResults([], query)
        }
      })
  }

  private renderDirectoryResults (results: Recipient[], query: string): void {
    this.directoryResultsTarget.textContent = ''

    if (results.length === 0) {
      this.directoryResultsTarget.appendChild(this.emptyState(query))

      return
    }

    results.forEach(recipient => {
      this.directoryResultsTarget.appendChild(this.contactRow(recipient))
    })
  }

  private emptyState (query: string): HTMLElement {
    const wrap = document.createElement('div')
    wrap.className = 'ft-req-empty'

    const link = document.createElement('a')
    link.href = '#'
    link.textContent = this.directorySwitchLabelValue
    link.addEventListener('click', event => {
      event.preventDefault()
      this.switchToOneOffFromSearch()
    })

    wrap.appendChild(document.createTextNode(
      query === '' ? this.directoryEmptyLabelValue : this.directoryNoMatchLabelValue.replace('%query%', query)
    ))
    wrap.appendChild(document.createTextNode(' '))
    wrap.appendChild(link)

    return wrap
  }

  private contactRow (recipient: Recipient): HTMLButtonElement {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'ft-req-contact' + (this.pickedRecipient?.id === recipient.id && this.pickedRecipient?.type === recipient.type ? ' is-on' : '')

    const avatar = document.createElement('span')
    avatar.className = 'ft-req-av'
    avatar.textContent = this.initials(recipient.name)

    const body = document.createElement('span')
    body.className = 'ft-req-contact__b'

    const name = document.createElement('span')
    name.className = 'ft-req-contact__name'
    name.textContent = recipient.name

    const meta = document.createElement('span')
    meta.className = 'ft-req-contact__meta'
    meta.textContent = recipient.meta

    const channels = document.createElement('span')
    channels.className = 'ft-req-chan'
    channels.appendChild(this.channelIcon('fa-envelope', recipient.email))
    const gap = document.createElement('span')
    gap.className = 'ft-req-chan__gap'
    channels.appendChild(gap)
    channels.appendChild(this.channelIcon('fa-mobile-screen', recipient.mobile))

    body.appendChild(name)
    if (recipient.meta !== '') {
      body.appendChild(meta)
    }
    body.appendChild(channels)

    button.appendChild(avatar)
    button.appendChild(body)

    if (this.pickedRecipient?.id === recipient.id && this.pickedRecipient?.type === recipient.type) {
      const tick = document.createElement('span')
      tick.className = 'ft-req-contact__tick'
      const icon = document.createElement('i')
      icon.className = 'fas fa-check'
      icon.setAttribute('aria-hidden', 'true')
      tick.appendChild(icon)
      button.appendChild(tick)
    }

    button.addEventListener('click', () => this.pickRecipient(recipient))

    return button
  }

  private channelIcon (iconClass: string, value: string): HTMLElement {
    const span = document.createElement('span')
    const icon = document.createElement('i')
    icon.className = `fas ${iconClass}`
    icon.setAttribute('aria-hidden', 'true')
    span.appendChild(icon)

    if (value === '') {
      span.className = 'ft-req-none'
      span.appendChild(document.createTextNode(iconClass === 'fa-envelope' ? this.directoryNoEmailLabelValue : this.directoryNoMobileLabelValue))
    } else {
      span.appendChild(document.createTextNode(value))
    }

    return span
  }

  private initials (name: string): string {
    const parts = name.trim().split(/\s+/)

    return ((parts[0] ?? '')[0] ?? '').toUpperCase() + ((parts[1] ?? '')[0] ?? '').toUpperCase()
  }

  private pickRecipient (recipient: Recipient): void {
    this.pickedRecipient = recipient

    this.nameTarget.value = recipient.name
    this.organisationTarget.value = recipient.organisation
    this.emailTarget.value = recipient.email
    this.mobileTarget.value = recipient.mobile
    this.syncRecipientInputs()

    this.runSearch()
    this.refreshWho()
  }

  private selectChannelCard (card: HTMLElement): void {
    const channel = card.dataset.channel ?? ''

    if (this.isChannelRefused(channel)) {
      card.classList.add(REFUSED_CLASS)
      window.setTimeout(() => card.classList.remove(REFUSED_CLASS), REFUSED_MS)

      return
    }

    this.channelInputTarget.value = channel
    this.applyChannelSelection()
    this.syncNav()
  }

  private reset (): void {
    this.mode = 'directory'
    this.pickedRecipient = null
    this.directorySearchTarget.value = ''
    this.setMode('directory')
    this.runSearch()
    this.showStep(0)
    this.refreshChannels()
  }

  private showStep (index: number): void {
    this.stepIndex = index
    const current = STEPS[index]

    this.panelTargets.forEach(panel => {
      panel.classList.toggle('d-none', panel.dataset.step !== current)
    })

    this.renderStepper()
    this.syncNav()
  }

  private renderStepper (): void {
    this.dotTargets.forEach((dot, index) => {
      dot.classList.toggle('ft-req-dot--cur', index === this.stepIndex)
      dot.classList.toggle('ft-req-dot--done', index < this.stepIndex)
    })

    this.dotBarTargets.forEach((bar, index) => {
      bar.classList.toggle('is-done', index < this.stepIndex)
    })
  }

  private syncNav (): void {
    const isLast = this.stepIndex === STEPS.length - 1

    this.nextButtonTarget.disabled = !this.stepValid(STEPS[this.stepIndex])
    this.nextButtonTarget.textContent = isLast ? this.sendLabelFor(this.channelInputTarget.value) : this.nextLabelValue
    this.backButtonTarget.textContent = this.stepIndex === 0 ? this.cancelLabelValue : this.backLabelValue
  }

  private sendLabelFor (channel: string): string {
    if (channel === 'email') {
      return this.sendEmailLabelValue
    }

    if (channel === 'sms') {
      return this.sendSmsLabelValue
    }

    if (channel === 'copy') {
      return this.sendCopyLabelValue
    }

    return this.nextLabelValue
  }

  private stepValid (step: Step): boolean {
    if (step === 'who') {
      if (this.mode === 'directory') {
        return this.pickedRecipient !== null
      }

      return this.nameTarget.value.trim() !== ''
    }

    if (step === 'how') {
      return this.channelInputTarget.value !== ''
    }

    return this.dueDateTarget.value !== ''
  }

  private isChannelRefused (channel: string): boolean {
    if (channel === 'email') {
      return this.emailTarget.value.trim() === ''
    }

    if (channel === 'sms') {
      return this.mobileTarget.value.trim() === ''
    }

    return false
  }

  private applyChannelSelection (): void {
    const channel = this.channelInputTarget.value

    this.channelCardTargets.forEach(card => {
      const isSelected = card.dataset.channel === channel

      card.classList.toggle('is-on', isSelected)
      card.setAttribute('aria-checked', isSelected ? 'true' : 'false')
    })

    const isCopy = channel === 'copy'

    this.remindersWrapTarget.classList.toggle('d-none', isCopy)
    this.remindersNoteTarget.classList.toggle('d-none', !isCopy)
  }

  private refreshChannels (): void {
    this.channelReasonTargets.forEach(reason => {
      reason.textContent = this.reasonFor(reason.dataset.channel ?? '')
    })

    if (this.channelInputTarget.value && this.isChannelRefused(this.channelInputTarget.value)) {
      this.channelInputTarget.value = ''
    }

    this.applyChannelSelection()
  }

  private reasonFor (channel: string): string {
    if (channel === 'email') {
      const email = this.emailTarget.value.trim()

      return email ? this.toLabelValue.replace('%destination%', email) : this.noEmailLabelValue
    }

    if (channel === 'sms') {
      const mobile = this.mobileTarget.value.trim()

      return mobile ? this.toLabelValue.replace('%destination%', mobile) : this.noMobileLabelValue
    }

    return ''
  }

  private dateFromToday (days: number): string {
    const date = new Date()

    date.setDate(date.getDate() + days)

    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${date.getFullYear()}-${month}-${day}`
  }
}
